import { Check } from "lucide-react";
import Reveal from "@/lib/Reveal";
import { SectionLabel, BuyButton, GoldRule, GlowPhoto } from "@/lib/ui";
import { CHECKOUT_URL, FOUNDER_IMAGE, LIFESTYLE_IMAGE, GROUP_IMAGE, RED_MIRROR_IMAGE, LAKE_IMAGE, CABIN_IMAGE, cta, founderStats, credentials, forHer, faqs } from "@/lib/siteContent";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export const Founder = () => (
  <section data-testid="founder-section" className="py-20 sm:py-28">
    <div className="mx-auto max-w-3xl px-6">
      <Reveal className="text-center">
        <SectionLabel>Why I Created This</SectionLabel>
        <div className="mt-10">
          <GlowPhoto
            src={FOUNDER_IMAGE}
            alt="J Muenz, creator of The Abundance Machine"
            testId="founder-photo"
          />
        </div>
      </Reveal>
      <Reveal delay={100}>
        <h2 className="mt-12 font-heading text-3xl sm:text-4xl tracking-tight text-charcoal">
          Hi! I'm <span className="text-ruby">J Muenz</span> and I've spent more than
          14 years studying and working with the subconscious mind, energy, emotions
          and the beliefs influencing how we experience our lives.
        </h2>
        <p className="mt-6 font-body text-base sm:text-lg leading-relaxed text-mutedwarm">
          I've created 3 multi-6 figure businesses. Had a $115,000 month. Created 21
          digital programs, led 19 sold out retreats, and worked with clients in more
          than 13 countries.
        </p>
        <p className="mt-5 font-body text-base sm:text-lg leading-relaxed text-mutedwarm">
          I've created a life with experiences I once could only imagine… like leading
          the Wild Woman Retreat in Mykonos, Greece. And I've also had plenty of
          opportunities to recognize where my own thoughts and beliefs were
          influencing how I experienced that success.
        </p>
        <p className="mt-5 font-body text-base sm:text-lg leading-relaxed text-mutedwarm">
          Because I've learned that you can make a tremendous amount of money and
          still have a complicated relationship with receiving. You can be incredibly
          accomplished and still feel like you should be doing more. And you can have
          a million reasons to trust yourself while still questioning what you know.
        </p>
        <p className="mt-5 font-body text-base sm:text-lg leading-relaxed text-mutedwarm">
          I've dedicated years to understanding these patterns and learning different
          ways to work with them.
        </p>
      </Reveal>
    </div>
    <Reveal delay={120}>
      <div
        data-testid="founder-stats-band"
        className="mt-16 w-full bg-ruby py-12"
      >
        <div className="mx-auto grid max-w-4xl grid-cols-2 gap-x-6 gap-y-10 px-6 text-center sm:grid-cols-3">
          {founderStats.map((stat, i) => (
            <div key={i} data-testid={`founder-stat-${i + 1}`}>
              <p className="font-heading text-4xl font-semibold text-gold-light sm:text-5xl">
                {stat.value}
              </p>
              <p className="mt-2 font-body text-[11px] font-medium uppercase tracking-[0.2em] text-cream/85 sm:text-xs">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </Reveal>
    <div className="mx-auto mt-16 max-w-3xl px-6">
      <Reveal delay={150}>
        <GlowPhoto
          src={LIFESTYLE_IMAGE}
          alt="J Muenz coaching women around a table"
          testId="lifestyle-photo"
        />
        <div className="mt-12 rounded-2xl border border-gold-border bg-white p-8 sm:p-10">
          <p className="font-heading text-xl sm:text-2xl italic text-ruby">
            My professional background includes:
          </p>
          <ul className="mt-6 space-y-3">
            {credentials.map((cred, i) => (
              <li key={i} className="flex items-start gap-3">
                <Check className="mt-1 h-4 w-4 shrink-0 text-ruby" aria-hidden="true" />
                <span className="font-body text-sm sm:text-base leading-relaxed text-charcoal">
                  {cred}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
      <Reveal delay={200}>
        <p className="mt-12 font-body text-base sm:text-lg leading-relaxed text-mutedwarm">
          And while I've worked with women through private mentorship, masterminds and
          retreats... I wanted to create something different, something accessible
          that combines the practices I love with the excitement of seeing what might
          happen when you use them consistently.
        </p>
        <p className="mt-5 font-body text-base sm:text-lg leading-relaxed text-mutedwarm">
          Something that makes personal transformation feel like an adventure… Because
          I think we've made changing our lives too serious.
        </p>
        <p className="mt-8 font-heading text-xl sm:text-2xl italic leading-snug text-charcoal">
          What if we got curious… and experimented… What if we allowed ourselves to
          have an absolutely ridiculous amount of fun exploring what's possible…
        </p>
        <div className="mt-10">
          <GlowPhoto
            src={RED_MIRROR_IMAGE}
            alt="J Muenz in her signature red"
            testId="red-photo"
          />
        </div>
        <p className="mt-8 whitespace-nowrap text-center font-script text-[clamp(1rem,3.2vw,3.4rem)] text-gold">
          That's why I created The Abundance Machine.
        </p>
        <p className="mt-4 text-center font-body text-base sm:text-lg text-mutedwarm">
          And I would LOVE for you to join me.
        </p>
        <div className="mt-10">
          <GlowPhoto
            src={GROUP_IMAGE}
            alt="J Muenz with women from her community"
            testId="group-photo"
          />
        </div>
      </Reveal>
    </div>
  </section>
);

export const ForHer = () => (
  <section data-testid="for-her-section" className="bg-blush py-20 sm:py-28">
    <div className="mx-auto max-w-3xl px-6">
      <Reveal className="text-center">
        <SectionLabel>Is This You?</SectionLabel>
        <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-charcoal">
          This is for the woman who...
        </h2>
      </Reveal>
      <div className="mt-14 space-y-6">
        {forHer.map((line, i) => (
          <Reveal key={i} delay={i * 60}>
            <div className="flex items-start gap-4">
              <span className="mt-1.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gold/15">
                <Check className="h-4 w-4 text-gold" aria-hidden="true" />
              </span>
              <p className="font-body text-base sm:text-lg leading-relaxed text-charcoal">
                {line}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const FAQ = () => (
  <section data-testid="faq-section" className="py-20 sm:py-28">
    <div className="mx-auto max-w-3xl px-6">
      <Reveal className="text-center">
        <SectionLabel>Questions? I've got you!</SectionLabel>
        <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-charcoal">
          Everything you're wondering…
        </h2>
      </Reveal>
      <Reveal delay={100}>
        <Accordion type="single" collapsible className="mt-12">
          {faqs.map((faq, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              data-testid={`faq-item-${i + 1}`}
              className="border-gold-border"
            >
              <AccordionTrigger
                data-testid={`faq-trigger-${i + 1}`}
                className="text-left font-heading text-lg sm:text-xl text-charcoal hover:text-ruby hover:no-underline"
              >
                {faq.q}
              </AccordionTrigger>
              <AccordionContent
                data-testid={`faq-content-${i + 1}`}
                className="font-body text-base leading-relaxed text-mutedwarm"
              >
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

export const FinalCTA = () => (
  <section data-testid="final-cta-section" className="relative overflow-hidden bg-ruby py-24 sm:py-32">
    <div
      className="pointer-events-none absolute inset-0 opacity-[0.12]"
      style={{
        backgroundImage:
          "url(https://images.unsplash.com/photo-1656055450481-e47bc720e687?crop=entropy&cs=srgb&fm=jpg&q=85)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      aria-hidden="true"
    />
    <div className="relative mx-auto max-w-3xl px-6 text-center">
      <Reveal>
        <p className="font-heading text-2xl sm:text-3xl italic leading-snug text-cream">
          So, how much more is possible for you…
        </p>
        <p className="mx-auto mt-6 max-w-2xl font-body text-base sm:text-lg leading-relaxed text-cream/85">
          I don't know exactly what will happen over the next 21 days and that's part
          of what makes this so exciting. Maybe you'll receive money or an opportunity
          you weren't expecting or you'll make a decision you've been putting off.
        </p>
        <div className="mt-10">
          <GlowPhoto
            src={LAKE_IMAGE}
            alt="J Muenz by the water at golden hour"
            testId="final-lake-photo"
          />
        </div>
        <p className="mx-auto mt-10 max-w-2xl font-body text-base sm:text-lg leading-relaxed text-cream/85">
          Whatever unfolds, I want you to have 21 days of deliberately practicing the
          beliefs, energy and behaviors that support the life you want.
        </p>
        <div className="mt-10">
          <GlowPhoto
            src={CABIN_IMAGE}
            alt="Women relaxing together at a retreat in the woods"
            testId="final-cabin-photo"
          />
        </div>
        <p className="mx-auto mt-10 max-w-xl font-heading text-xl sm:text-2xl italic text-gold-light">
          Now let's see what happens when you become really f*cking good at receiving.
        </p>
        <div className="mx-auto mt-12 flex max-w-xs items-center justify-center gap-3" aria-hidden="true">
          <span className="h-px w-16 bg-gold-light/60" />
          <span className="h-1.5 w-1.5 rotate-45 bg-gold-light" />
          <span className="h-px w-16 bg-gold-light/60" />
        </div>
        <h2 className="mt-10 font-body text-xs sm:text-sm font-medium uppercase tracking-[0.4em] text-cream/80">
          The Abundance Machine
        </h2>
        <p className="mt-3 font-heading text-3xl sm:text-4xl italic text-cream">
          Let's see how much you can manifest in the next 21 days.
        </p>
        <p className="mx-auto mt-6 max-w-xl font-body text-sm sm:text-base leading-relaxed text-cream/80">
          21 daily audio experiences infused with subconscious recoding, energy and
          frequency practices and daily abundance experiments. Your personal abundance
          tracker.
        </p>
        <div className="mt-10">
          <a
            data-testid="cta-final-button"
            href={CHECKOUT_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              import("canvas-confetti").then(({ default: confetti }) =>
                confetti({
                  particleCount: 130,
                  spread: 80,
                  startVelocity: 42,
                  origin: { y: 0.7 },
                  colors: ["#D4AF37", "#F0CE6B", "#B8154A", "#FF6B6B", "#FFF9F5"],
                })
              );
            }}
            className="inline-flex items-center gap-3 rounded-full bg-cream px-10 py-4 font-body text-sm sm:text-base font-semibold uppercase tracking-[0.2em] text-ruby shadow-[0_12px_36px_rgba(0,0,0,0.25)] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_44px_rgba(0,0,0,0.35)]"
          >
            {cta.final}
          </a>
        </div>
        <p className="mt-10 font-script text-4xl sm:text-5xl text-gold-light">
          The sky is where we're starting…
        </p>
      </Reveal>
    </div>
  </section>
);

export const Footer = () => (
  <footer data-testid="footer" className="bg-cream py-12">
    <div className="mx-auto max-w-3xl px-6 text-center">
      <GoldRule />
      <p className="mt-8 font-body text-xs leading-relaxed text-mutedwarm">
        Testimonials are from clients of my other services and are not specific
        results from The Abundance Machine. Individual experiences vary.
      </p>
      <p className="mt-4 font-body text-xs text-mutedwarm">
        © {new Date().getFullYear()} J Muenz. All rights reserved.
      </p>
    </div>
  </footer>
);
